import { useState, useEffect } from "react";
import { Box, Button, ButtonGroup, Typography } from "@mui/material";
import { Family } from "protogen/advisors_service_pb";
import { useNavigate } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import { useRunWorkflow } from "services/extraction";
import { RunWorkflowRequest } from "protogen/extraction_service_pb";
import Loading from "components/common/Loading";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import FlexBox from "components/common/FlexBox";
import { ArrowRight, Plus } from "lucide-react";
import { ReactComponent as SparklesIcon } from "../../icons/Advisor/Sparkles.svg";
import LinkRouter from "components/navigation/LinkRouter";

const InspiredTask = ({
  title,
  style = "contained",
  onTaskClick,
}: {
  title: string;
  style?: "contained" | "normal";
  onTaskClick: (title: string) => void;
}) => {
  const isMobile = useIsMobile();

  return (
    <FlexBox
      direction={style == "contained" ? "row" : "row-reverse"}
      variant={style == "contained" ? "contained" : "default"}
      justifyContent={"start"}
      alignItems={style == "contained" ? "center" : "start"}
      gap="8px"
      sx={{ paddingRight: isMobile ? "" : "16px" }}
    >
      <Box
        onClick={() => {
          onTaskClick(title);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <Plus size={24} color="#198282" />
      </Box>
      <Box>
        <Typography variant="body" color="#3D3D3D">
          {title}
        </Typography>
      </Box>
    </FlexBox>
  );
};

type Task = {
  title: string;
  details: string;
};

const CATEGORIES = [
  { title: "Top picks", value: "top_picks" },
  { title: "Seasonal", value: "seasonal" },
  { title: "Home", value: "home" },
  { title: "Wellness", value: "wellness" },
];

export default ({ family }: { family: Family }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const [category, setCategory] = useState<string>("top_picks");
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const { request: workflowRequest } = useRunWorkflow();

  useEffect(() => {
    const handler = (channel: string, tasksData: any) => {
      setLoading(false);
      // Maybe the sync call handled this first.
      setAllTasks((tasks) =>
        tasks.length > 0 ? tasks : (tasksData as Task[]),
      );
    };

    registerHandler("task-suggestions/", handler);
    return () => {
      unregisterHandler("task-suggestions/", handler);
    };
  }, []);

  useEffect(() => {
    // Generate personalized tasks using family-context-prompted
    generatePersonalizedTasks();
  }, [family, category]);

  const generatePersonalizedTasks = async () => {
    if (!family?.ref || loading) return;
    setLoading(true);
    setAllTasks([]);
    const request = new RunWorkflowRequest({
      workflowName: "family-task-suggestions",
      arguments: [
        {
          name: "category",
          value: {
            case: "stringValue",
            value: category,
          },
        },
      ],
      entityType: 0,
      entityRef: family.ref,
      runAsync: false,
    });
    const result = await workflowRequest(request);

    if (result) {
      const data = JSON.parse(result.result);
      // Maybe the socket handled this first.
      setAllTasks((tasks) => (tasks.length > 0 ? tasks : data.tasks));
    }
    setLoading(false);
  };

  const onTaskClick = (title: string) => {
    navigate("/tasks/add", {
      state: { taskTitle: title, family: family },
    });
  };

  // Use all tasks since they're filtered server-side by category
  const filteredTasks = allTasks;

  return (
    <FlexBox direction="column" gap="20px">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          alignItems: "start",
          justifyContent: "space-between",
        }}
      >
        <FlexBox alignItems="center" sx={{ width: "100%" }}>
          <FlexBox gap="8px" alignItems="center">
            <SparklesIcon height="20px" width="20px" />
            <Typography
              variant="h4"
              color="#3D3D3D"
              sx={{
                display: "flex",
                lineHeight: 1,
              }}
            >
              Task ideas for you
            </Typography>
          </FlexBox>
          <LinkRouter
            to={"/task-discovery"}
            sx={{ display: "flex", alignItems: "center", gap: "4px" }}
          >
            <Typography variant="bodyHeavy" color="#198282">
              Task Discovery
            </Typography>
            <ArrowRight size={20} color="#198282" />
          </LinkRouter>
        </FlexBox>
        <Typography variant="body" color="text.secondary">
          This feature is in beta: brilliance probable, oddities possible.
        </Typography>
      </Box>
      <Box
        sx={{
          width: isMobile ? "100vw !important" : "100%",
          ...(isMobile && {
            marginLeft: "-24px",
            marginRight: "-24px",
            paddingLeft: "24px",
            paddingRight: "24px",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            "-ms-overflow-style": "none",
            "scrollbar-width": "none",
          }),
        }}
      >
        <ButtonGroup
          sx={{
            display: "flex",
            flexWrap: isMobile ? "nowrap" : "wrap",
            width: isMobile ? "max-content" : "100%",
            minWidth: isMobile ? "100%" : "auto",
            ...(isMobile && {
              gap: "8px",
            }),
          }}
        >
          {CATEGORIES.map((cat) => (
            <Button
              key={cat.value}
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                minWidth: "auto",
                paddingX: "16px",
                ...(isMobile && {
                  flex: "0 0 auto",
                }),
                ...(!isMobile && {
                  flex: "1 1 auto",
                }),
              }}
              variant={
                category === cat.value ? "buttonGroupSelected" : "buttonGroup"
              }
              onClick={() => setCategory(cat.value)}
            >
              {cat.title}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
      <FlexBox direction="column" gap="8px">
        {loading ? (
          <Loading minHeight="200px" />
        ) : (
          filteredTasks.map((task, i) => (
            <InspiredTask
              key={i}
              title={task.title}
              onTaskClick={onTaskClick}
            />
          ))
        )}
      </FlexBox>
    </FlexBox>
  );
};
